import type { IRelayGroupMetadata } from '@experiences/interfaces';
import {
    useNavigateWithParams,
    useQueryState,
} from '@experiences/util';
import type { IGridFilters } from '@uipath/portal-shell-react';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import useSWR from 'swr';

import { routePaths } from '../../common/constants/routePaths';
import {
    deleteRelayGroup,
    getRelayGroups,
} from '../../services/relay/RelayService';
import { accountGlobalId } from '../../store/selectors';

const useRelaySettingsViewModel = () => {
    const navigate = useNavigateWithParams();
    const { tenantId = '' } = useParams<{ tenantId: string }>();
    const accountId = useSelector(accountGlobalId);

    const [ pagination, setPagination ] = useQueryState<{
        top: number;
        skip: number;
        searchTerm?: string;
    }>('pagination', {
        top: 25,
        skip: 0,
        searchTerm: '',
    }, undefined, true);

    const {
        data: relayGroups, isValidating: loading, mutate: relayMutate,
    } = useSWR({
        accountId,
        tenantId,
        top: pagination.top,
        skip: pagination.skip,
        searchTerm: pagination.searchTerm,
    }, () => getRelayGroups(accountId, tenantId, pagination.top, pagination.skip, pagination.searchTerm));

    const onGridChange = useCallback(({
        pageIndex, pageSize, searchTerm,
    }: IGridFilters<IRelayGroupMetadata>) => {
        const pageSizeOrDefault = pageSize ?? 25;
        setPagination({
            top: pageSizeOrDefault,
            skip: (pageIndex ?? 0) * pageSizeOrDefault,
            searchTerm,
        });
    }, [ setPagination ]);

    const deleteGroup = useCallback(async (relayGroupId: string) => {
        await deleteRelayGroup(accountId, tenantId, relayGroupId);
        await relayMutate();
    }, [ accountId, tenantId, relayMutate ]);

    const editGroup = useCallback((relayGroupId: string) => {
        navigate(routePaths.adminTenantRelayEdit.replace(':tenantId', tenantId).replace(':relayGroupId', relayGroupId));
    }, [ navigate, tenantId ]);

    const onGridRowClick = useCallback((id: string) => {
        navigate(routePaths.adminTenantRelayDetails.replace(':tenantId', tenantId).replace(':relayGroupId', id));
    }, [ navigate, tenantId ]);

    const handleRegisterRelayGroup = useCallback(() => navigate(routePaths.adminTenantRelayAdd.replace(':tenantId', tenantId)), [ navigate, tenantId ]);

    return {
        accountId,
        tenantId,
        pagination,
        relayGroups,
        onGridChange,
        loading,
        refresh: relayMutate,
        editGroup,
        deleteGroup,
        handleRegisterRelayGroup,
        onGridRowClick,
    };
};

export default useRelaySettingsViewModel;
